import { queryOptions } from '@tanstack/react-query';

import { exchangeApiV1 } from 'lib/api/exchangeApi';

/**
 * V2 referrals summary (`GET /v1/rewards/referrals/summary`) — the single web data source for
 * both referral programs: `referrer.predictions` and `referrer.perps` share one `ProductSummary`
 * wire shape, plus the caller's own received referral (`referee`) and non-withdrawable credits.
 *
 * Hook-free module — React hooks live in `referralsSummary.hooks.ts`.
 */

export type RewardType = 'flat' | 'variable' | 'volume_tiered' | 'fee_share';

/**
 * Referee funnel status. `account_verified` becomes `has_deposited` once the referee deposits;
 * later stages already imply a deposit.
 */
export type ReferralStatus =
    | 'account_created'
    | 'account_verified'
    | 'has_deposited'
    | 'trades_placed'
    | 'reward_issued'
    | 'reward_claimed'
    | 'expired';

export type RefereeState = 'pending' | 'completed' | 'expired';

// Append-only. `bonus_unlocked` (flat's auto-credit) and `unlock_bonus` (variable's referrer claim)
// are different steps, not a typo. `first_trade`/`try_perpetuals` are standard-checklist-only
// (New User Checklist endpoint) — never part of a referee ladder.
export type RefereeStep =
    | 'create_account'
    | 'verify_identity'
    | 'deposit_funds'
    | 'trade_threshold'
    | 'bonus_unlocked'
    | 'unlock_bonus'
    | 'referral_qualified'
    | 'first_trade'
    | 'try_perpetuals';

export type RefereeStepStatus = 'completed' | 'current' | 'incomplete';

/** `threshold_cents`/`trade_progress_cents` ride only on the `trade_threshold` step. */
export type RefereeFunnelStep = {
    step: RefereeStep;
    status: RefereeStepStatus;
    threshold_cents?: number;
    trade_progress_cents?: number;
};

export type VariableClaim = {
    has_claimed: boolean | null;
    claimed_ts?: string;
};

export type VolumeTieredOfferTier = {
    key: string;
    threshold_cents: number;
    reward_cents: number;
};

export type VolumeTieredRewardTier = VolumeTieredOfferTier & {
    granted: boolean;
    expiration_ts?: string;
};

export type VolumeTieredReward = {
    max_cumulative_reward_cents: number;
    current_volume_cents: number;
    in_progress_tier_key: string | null;
    tiers: VolumeTieredRewardTier[];
};

export type FeeShareOffer = {
    share_bps: number;
    max_reward_per_referral_cents: number;
    share_window_days: number;
    qualification_window_days: number;
};

export type FeeReductionOffer = {
    reduction_bps: number;
    duration_days: number;
};

export type FaqItem = {
    question: string;
    answer: string;
};

/** `share_bps`/`max_reward_cents` are attach-time offer snapshots; `fees_paid_cents`/`reward_earned_cents` are live. */
export type FeeShareReward = {
    share_bps: number;
    max_reward_cents: number;
    fees_paid_cents: number;
    reward_earned_cents: number;
};

/** Reward shape for an offer or referral; exactly one variant is populated per `RewardType`. */
export type RewardDetails = {
    flat_reward?: { payout_amount_cents: number };
    variable_reward?: { max_reward_cents: number };
    volume_tiered_offer?: {
        max_cumulative_reward_cents: number;
        tiers: VolumeTieredOfferTier[];
    };
    fee_share_offer?: FeeShareOffer;
    fee_reduction_offer?: FeeReductionOffer;
    flat_offer?: { reward_amount_cents: number; earn_window_days: number };
    faq?: FaqItem[];
};

export type ReferralTrayItem = {
    referral_id: string;
    tier_key: string;
    reward_cents: number;
    referee_redacted_email: string;
};

export type UnacknowledgedReferralsTray = {
    total_reward_cents: number;
    items: ReferralTrayItem[];
};

/** A referral the caller sent (caller is the referrer). Rows arrive pre-sorted by the server. */
export type SentReferral = {
    id: string;
    reward_type: RewardType;
    created_ts?: string;
    expiration_ts?: string;
    claim_available: boolean;
    referrer: {
        variable_claim?: VariableClaim;
        volume_tiered_reward?: VolumeTieredReward;
        fee_share_reward?: FeeShareReward;
    };
    referee: {
        redacted_email: string;
        // TODO(natashawong): delete `status`, `threshold_cents` and `trade_progress_cents` once
        // verified and the BE is deployed — it drops them in its next breaking version. Prefer
        // `state`/`steps` and the `trade_threshold` step's own copies.
        status: ReferralStatus;
        state: RefereeState;
        /** `null` when the offer has no funnel — perps volume-tiered drills into tiers, not steps. */
        steps: RefereeFunnelStep[] | null;
        threshold_cents: number | null;
        trade_progress_cents?: number;
    };
};

export type ReferralEligibilityProgress = {
    amount_traded_cents: number;
    threshold_cents: number;
};

export type BoostedReferralPromotion = {
    campaign_id: string;
    ends_ts: string;
};

export type CurrentOffer = {
    reward_type: RewardType;
    referrer: {
        /** Empty `{}` (no offer) when the referrer has no active offer — seen on `eligible: false` accounts. */
        reward_details: RewardDetails;
    };
    referee: {
        threshold_cents: number | null;
        reward_details: RewardDetails;
    };
    boosted_referral_promotions?: BoostedReferralPromotion[];
};

/** One referrer-side program summary; the same wire shape serves predictions and perps. */
export type ProductSummary = {
    /**
     * Durable can-actually-refer user-state. NOT the perps render gate — that's
     * `referral_offer_available`; the hub still shows when this is false (with
     * `eligibility_progress` rendering the trade-requirement bar).
     */
    eligible: boolean;
    total_earned_cents: number | null;
    total_credited_referrals: number | null;
    /** Count of in-flight referrals (Earning state, nothing granted yet) — NOT cents. */
    pending: number;
    /** `pending_count` supersedes the `pending` scalar above. */
    pending_count?: number;
    completed_count?: number;
    expired_count?: number;
    /** Progress toward the referrer trade threshold; omitted once crossed. */
    eligibility_progress?: ReferralEligibilityProgress;
    /** Caller's lifetime trade volume on this product; always present, unlike eligibility_progress. */
    amount_traded_cents?: number;
    /** Server-evaluated Statsig rollout gate for the perps referral hub. Remove when FF is fully rolled out. */
    referral_offer_available?: boolean;
    referral_code?: string;
    lifetime_cap: {
        amount_cents: number | null;
        number_of_referrals: number | null;
    };
    current_offer: CurrentOffer | null;
    referrals: SentReferral[];
    reward_details?: {
        volume_tiered?: {
            unacknowledged_rewards_tray?: UnacknowledgedReferralsTray;
        };
        fee_share?: {
            weekly_earned_cents: number;
        };
    };
};

type PerpsReferralBase = Omit<SentReferral, 'reward_type' | 'created_ts' | 'expiration_ts' | 'referrer'> & {
    created_ts: string;
    expiration_ts: string;
};

export type PerpsVolumeTieredReferral = PerpsReferralBase & {
    reward_type: 'volume_tiered';
    referrer: {
        volume_tiered_reward: VolumeTieredReward;
    };
};

export type PerpsFeeShareReferral = PerpsReferralBase & {
    reward_type: 'fee_share';
    referrer: {
        /** Absent for freshly signed-up pending rows; present once the referee starts accruing fees. */
        fee_share_reward?: FeeShareReward;
    };
};

export type PerpsReferral = PerpsVolumeTieredReferral | PerpsFeeShareReferral;

export type PerpsCurrentOffer = CurrentOffer & { reward_type: 'volume_tiered' | 'fee_share' };

/** One program's own totals/offer/rows — unlike the enclosing `PerpsReferrerProgram`, which sums across every program the referrer has been on. */
export type PerpsProgramSummary = Pick<
    ProductSummary,
    | 'total_earned_cents'
    | 'total_credited_referrals'
    | 'pending'
    | 'pending_count'
    | 'completed_count'
    | 'expired_count'
    | 'referral_offer_available'
    | 'reward_details'
> & {
    program_id: string;
    /** Null once this program has ended, even while another program is live. */
    current_offer: PerpsCurrentOffer | null;
    referrals: PerpsReferral[];
};

export type PerpsReferrerProgram = Omit<ProductSummary, 'current_offer' | 'referrals'> & {
    current_offer: PerpsCurrentOffer | null;
    referrals: PerpsReferral[];
    /** Absent on responses that predate the per-program breakdown. */
    programs?: PerpsProgramSummary[];
};

/** The caller's own referral as referee (they signed up through someone's link). */
export type ReceivedReferral = {
    id: string;
    product: 'predictions';
    reward_type: RewardType;
    created_ts?: string;
    expiration_ts?: string;
    referrer: {
        blocked?: boolean;
    };
    referee?: {
        // TODO(natashawong): delete `status` and `threshold_cents` once verified and the BE is
        // deployed — prefer `state`/`steps` and the `trade_threshold` step's own copy.
        status: ReferralStatus;
        state: RefereeState;
        steps: RefereeFunnelStep[] | null;
        /** Unlike `SentReferral`, the received row carries this inside `referee`. */
        claim_available: boolean;
        threshold_cents: number | null;
        /**
         * Offer snapshot for the caller's own reward (variable max); not yet sent by the
         * backend. Flat amounts ride on `threshold_cents`.
         */
        reward_details?: RewardDetails;
        variable_claim?: VariableClaim;
    };
};

export type NonwithdrawableCredit = {
    id: string;
    amount_awarded: number;
    amount_traded: number;
    awarded_ts: string;
    expiration_ts: string;
};

export type NonwithdrawableCreditsBucket = {
    amount_cents: number;
    expiry_days?: number;
    credits: NonwithdrawableCredit[];
};

export type ReferralsSummaryResponse = {
    /** Absent on some production responses despite this declaration — access with optional chaining. */
    referrer: {
        referral_code: string;
        blocked: boolean;
        predictions?: ProductSummary;
        perps?: PerpsReferrerProgram;
    };
    referee: ReceivedReferral | null;
    nonwithdrawable_credits?: {
        predictions?: NonwithdrawableCreditsBucket;
        perps?: NonwithdrawableCreditsBucket;
    };
};

export const referralsSummaryKeys = {
    all: ['referralsSummary'] as const,
    user: (userId: string) => [...referralsSummaryKeys.all, userId] as const,
};

const getReferralsSummary = async (signal?: AbortSignal): Promise<ReferralsSummaryResponse> => {
    const response = await exchangeApiV1.get<ReferralsSummaryResponse>('/rewards/referrals/summary', { signal });
    return response.data;
};

export const referralsSummaryOptions = (userId: string) =>
    queryOptions({
        queryKey: referralsSummaryKeys.user(userId),
        queryFn: ({ signal }) => getReferralsSummary(signal),
        // Don't set refetchOnMount 'always' here — it would refetch on every mount. Tabs and panels opt in via useReferralsSummaryQuery.
        staleTime: 30_000,
    });
